import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, getToken, clearToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldAlert, LogOut } from "lucide-react";

const SECTIONS = [
  {
    h: "1. Purpose & Access",
    p: [
      "The Itemwise Costing SOP is an official operational resource provided as part of the Chai Chaska Cafe® Confidential Franchise Document.",
      "Access is provided strictly for confidential business purposes and authorised Chai Chaska Cafe® outlet usage. It is intended to support outlet-level costing, operational planning, menu analysis and related business activities.",
      "The access provided is not for personal use or any purpose unrelated to the authorised Chai Chaska Cafe® outlet operations."
    ]
  },
  {
    h: "2. Confidentiality",
    p: [
      "The Confidential Franchise Document contains commercially sensitive operational information belonging to Chai Chaska Cafe® / Anas Foods.",
      "The information must be treated as confidential and used responsibly for the authorised outlet's business operations.",
      "The document and its contents should be made available only to authorised personnel who require the information for their designated outlet responsibilities."
    ]
  },
  {
    h: "3. Indicative Costing",
    p: [
      "The Itemwise Costing SOP provides indicative costing for reference and operational planning.",
      "The costing displayed does not constitute a fixed or guaranteed cost applicable to every outlet.",
      "Actual costing may vary depending on several factors, including: outlet location, local area and market conditions, supplier and vendor, purchase quantity, supplier rates, transportation and logistics, seasonal price fluctuations, taxes and applicable charges, product specifications and pack sizes, wastage and operational practices."
    ]
  },
  {
    h: "4. Costing Accuracy",
    p: [
      "Reasonable efforts may be made to maintain and update the costing information. However, costing figures may change and may not always reflect the latest actual purchase price applicable to a particular outlet.",
      "Franchisees are therefore advised to verify current supplier prices, invoices and applicable purchase rates before making final commercial decisions."
    ]
  },
  {
    h: "5. Outlet-Specific Costing",
    p: [
      "The costing provided in the Confidential Franchise Document is intended as a standard reference.",
      "Individual outlets may experience different actual costs because of their location, local supplier network, purchasing quantities, logistics and market conditions.",
      "Accordingly, the final cost incurred by an outlet may differ from the figures presented in the Itemwise Costing SOP."
    ]
  },
  {
    h: "6. Use for Business Operations",
    p: [
      "The Itemwise Costing SOP may be used for authorised activities such as: understanding product-wise costing, outlet-level cost planning, menu pricing analysis, food cost monitoring, purchase planning, margin analysis, operational training, business review.",
      "The information should be used only in connection with authorised Chai Chaska Cafe® outlet operations."
    ]
  },
  {
    h: "7. No Guarantee of Profitability",
    p: [
      "The costing information is not a guarantee of any particular profit margin, gross margin, net profit, sales volume, return on investment or business performance.",
      "Actual results will depend on individual outlet performance, sales, expenses, location, pricing, wastage, procurement and other commercial factors."
    ]
  },
  {
    h: "8. Responsibility of Franchisee",
    p: [
      "The franchisee remains responsible for reviewing and verifying the applicable actual costs for their outlet.",
      "Before making significant purchasing, pricing or financial decisions, the franchisee should consider current supplier quotations, invoices and actual operating data."
    ]
  },
  {
    h: "9. Confidential Franchise Document",
    p: [
      "The Chai Chaska Cafe® Confidential Franchise Document, including the Itemwise Costing SOP, contains proprietary operational knowledge and brand standards.",
      "It is provided to authorised franchisees and outlet teams to support consistent implementation of Chai Chaska Cafe® standards.",
      "Its contents should be handled with appropriate confidentiality and professional responsibility."
    ]
  },
  {
    h: "10. Intellectual Property",
    p: [
      "The Confidential Franchise Document, formats, content, calculations, processes, operational standards, visual materials and associated brand information remain the intellectual property of their respective owners, including Chai Chaska Cafe® / Anas Foods, wherever applicable.",
      "Access to this document does not transfer ownership or intellectual property rights to the user."
    ]
  },
  {
    h: "11. Updates",
    p: [
      "Costing information, ingredient prices, product specifications and other operational information may be revised from time to time.",
      "Chai Chaska Cafe® / Anas Foods may update the Itemwise Costing SOP whenever required to reflect current operational or commercial information."
    ]
  },
  {
    h: "12. Data & Privacy Policy",
    p: [
      "To secure this Confidential Franchise Document, Chai Chaska Cafe® collects and processes a limited set of technical information when you sign in. The purpose is strictly enforcement, audit and prevention of unauthorised sharing — no marketing, profiling or third-party sale of your data ever takes place.",
      "• Location coordinates (latitude / longitude) — captured at the moment of sign-in only, to verify you are within your assigned outlet's geofence. Coordinates are not stored beyond audit logs.",
      "• Device signature — a hashed fingerprint of your device's screen, timezone and hardware profile is stored to enforce the one-device-per-user rule. No IMEI, phone number, contacts or personal identifiers are read.",
      "• Sign-in email, IP address and browser user-agent — retained in the audit trail to prove who accessed the document, when and from where.",
      "• Typed digital signature (your franchisee / store name on this screen) — retained with the timestamp of acceptance.",
      "We do not collect: your photos, contacts, SMS, files, microphone or camera. We do not track you across other websites. Data is stored on secure servers accessible only to the Chai Chaska Cafe® master admin and is used solely to enforce this agreement."
    ]
  },
  {
    h: "13. Acknowledgement Before Access",
    p: [
      "By selecting 'I Agree & Continue', I confirm that:",
      "• I am accessing the Itemwise Costing SOP for authorised Chai Chaska Cafe® outlet business purposes.",
      "• I understand that the Confidential Franchise Document is proprietary and must be treated as confidential.",
      "• I understand that the costing figures are indicative and may vary by outlet location, area, supplier and market conditions.",
      "• I understand that actual supplier prices and purchase invoices should be considered when determining final costs.",
      "• I consent to the limited data collection described in the Data & Privacy Policy section above for enforcement and audit purposes.",
      "• I agree to use the Itemwise Costing SOP responsibly and only for authorised outlet operations.",
      "• I acknowledge that this agreement will be re-presented every 15 days."
    ]
  }
];

export default function Terms() {
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [franchiseeName, setFranchiseeName] = useState("");
  const [me, setMe] = useState(null);
  const scrollRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!getToken()) { nav("/login"); return; }
    api.get("/auth/me").then(r => {
      setMe(r.data);
      if (r.data.name) setFranchiseeName(r.data.name);
      else if (r.data.store) setFranchiseeName(r.data.store);
    }).catch(() => { clearToken(); nav("/login"); });
  }, [nav]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (remaining <= 24) setScrolledEnd(true);
  };

  useEffect(() => { onScroll(); }, []);

  const proceed = async () => {
    if (franchiseeName.trim().length < 2) return;
    localStorage.setItem("cc_terms_agreed_at", new Date().toISOString());
    try {
      await api.post("/terms/acknowledge", {
        version: "2026-02",
        accepted_at_client: new Date().toISOString(),
        franchisee_name: franchiseeName.trim()
      });
    } catch (e) { /* non-blocking */ }
    window.location.href = me?.role === "admin" ? "/admin" : "/viewer";
  };

  const signOut = () => { clearToken(); nav("/login"); };

  const canContinue = scrolledEnd && agreed && franchiseeName.trim().length >= 2;

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-3 md:p-8"
      style={{ background: "radial-gradient(1200px 800px at 20% 10%, #F7E7B6 0%, #F5EFE0 45%, #EFE3C6 100%)" }}>
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-40 blur-2xl"
        style={{ background: "radial-gradient(circle, #F5C242 0%, transparent 70%)" }} />
      <div className="relative z-10 w-full max-w-3xl rounded-3xl shadow-2xl"
        style={{ background: "#FFFDF6", border: "1px solid #ECD9A5" }}>
        <div className="p-5 md:p-8 border-b flex items-start justify-between gap-3 flex-wrap" style={{ borderColor: "#ECD9A5" }}>
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#3B2519" }}>
              <ShieldAlert size={18} style={{ color: "#F5C242" }} />
            </div>
            <div className="min-w-0">
              <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase mb-1" style={{ color: "#B4841F" }}>Chai Chaska Cafe®</div>
              <h1 className="font-display text-xl md:text-3xl leading-tight" style={{ color: "#2b1a0d" }}>Itemwise Costing — Terms &amp; Conditions</h1>
              <p className="text-xs mt-1 font-medium break-words" style={{ color: "#7a6349" }}>
                Applicable to: Authorised Chai Chaska Cafe® Franchisees / Outlet Team
                {me && <span> · Signed in as <b>{me.email}</b></span>}
              </p>
            </div>
          </div>
          {me && (
            <button data-testid="terms-signout-btn" onClick={signOut}
              className="flex items-center gap-1 text-[10px] font-mono2 uppercase tracking-widest whitespace-nowrap"
              style={{ color: "#7a6349" }}>
              <LogOut size={12} /> Sign out
            </button>
          )}
        </div>

        <div ref={scrollRef} onScroll={onScroll}
          data-testid="terms-scroll"
          className="p-5 md:p-8 overflow-y-auto text-sm leading-relaxed"
          style={{ color: "#2b1a0d", maxHeight: "50vh" }}>
          {SECTIONS.map((s) => (
            <section key={s.h} className="mb-5">
              <h2 className="font-display text-lg mb-2" style={{ color: "#8a5a1a" }}>{s.h}</h2>
              {s.p.map((line, i) => (
                <p key={i} className="mb-2" style={{ color: "#3d2a19" }}>{line}</p>
              ))}
            </section>
          ))}

          <section className="mt-4 p-4 rounded-lg border-2" style={{ background: "#FCE9C6", borderColor: "#B4841F", color: "#5b3512" }}>
            <div className="font-bold uppercase tracking-widest text-xs mb-1">Enforcement Notice</div>
            <p className="text-sm">
              Any illegal sharing, redistribution, screenshotting or off-outlet use of the Confidential Franchise Document will be treated as a <b>breach of the Franchise Agreement</b>. A penalty of <b>₹5,000/-</b> per confirmed incident applies. Additional legal action may be pursued under applicable laws. Every session is watermarked with your email, device fingerprint and IST timestamp.
            </p>
          </section>

          <p className="mt-6 text-center text-xs font-mono2 uppercase tracking-widest" style={{ color: "#8a5a1a" }}>
            Chai Chaska Cafe® — Confidential Franchise Document · For Authorised Franchise &amp; Outlet Operations Only
          </p>

          <div className="h-4" data-testid="terms-scroll-sentinel" />
        </div>

        <div className="p-5 md:p-8 border-t space-y-3" style={{ borderColor: "#ECD9A5", background: "#FFF8E7" }}>
          {!scrolledEnd && (
            <div className="text-center text-xs font-mono2 uppercase tracking-widest" style={{ color: "#c0392b" }}>
              ▼ Scroll to the bottom to read the full terms
            </div>
          )}
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest block mb-1" style={{ color: "#8a5a1a" }}>
              Franchisee / Store name (digital signature)
            </label>
            <input data-testid="franchisee-name-input" type="text" value={franchiseeName}
              onChange={(e) => setFranchiseeName(e.target.value)}
              placeholder="e.g. Anas Foods — CCC Wakad"
              disabled={!scrolledEnd}
              className="w-full h-12 px-4 rounded-lg border bg-white text-base"
              style={{ borderColor: "#ECD9A5", color: "#2b1a0d" }} />
            <div className="mt-1 text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>
              Your typed name is recorded in the acknowledgement audit trail.
            </div>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox data-testid="agree-checkbox" checked={agreed} disabled={!scrolledEnd}
              onCheckedChange={(v) => setAgreed(!!v)} />
            <span className="text-sm" style={{ color: "#2b1a0d" }}>
              I, the signatory named above, have read the terms in full and agree to use the Itemwise Costing SOP responsibly and only for authorised outlet operations. I understand this acknowledgement will be re-presented every 15 days.
            </span>
          </label>
          <Button data-testid="agree-continue-btn" disabled={!canContinue} onClick={proceed}
            className="w-full h-12 rounded-full font-bold text-base btn-lift"
            style={{ background: canContinue ? "#F5C242" : "#e8d69c", color: "#1A1200" }}>
            I Agree &amp; Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
